import SaveAltIcon from '@mui/icons-material/SaveAlt';
import { ShipmentOrderView } from '../../utils/shipmentOrders';
import { Box } from '@mui/system';
import {
  DataGridPro,
  getGridNumericOperators,
  getGridStringOperators,
  GridColDef,
  GridColumnsPanel,
  GridColumnVisibilityModel,
  GridDensity,
  GridDensityTypes,
  GridFilterInputValue,
  GridFilterItem,
  GridLinkOperator,
  GridToolbarColumnsButton,
  GridToolbarContainer,
  GridToolbarDensitySelector,
  GridToolbarFilterButton,
  GridToolbarQuickFilter,
  useGridApiRef,
} from '@mui/x-data-grid-pro';
import {
  ComponentProps,
  FC,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import * as XLSX from 'xlsx';
import { useUserConfiguration } from '../../providers/UserConfigurationProvider';
import _, { isEqual, pick } from 'lodash';
import { TABLE_NAMES } from '../../lib/constants';
import {
  Button,
  Checkbox,
  IconButton,
  Tooltip,
  Typography,
} from '@mui/material';
import { configuration } from '../../configuration';
import { Compress, Expand, Print } from '@mui/icons-material';
import ViewColumnIcon from '@mui/icons-material/ViewColumn';
import { makeStyles } from '@mui/styles';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import HideIcon from '@mui/icons-material/VisibilityOff';
import LinkIcon from '@mui/icons-material/Link';

const useStyles = makeStyles(() => ({
  root: {
    '& .MuiDataGrid-columnHeaderTitleContainer': {
      lineHeight: 1,
      textAlign: 'left',
      flexDirection: 'column-reverse !important',
      whiteSpace: 'pre-wrap',
      justifyContent: 'flex-start !important',
      alignContent: 'flex-start',
      flexWrap: 'wrap',
      height: 50,
    },
    '& .MuiDataGrid-columnSeparator': {
      marginTop: 34,
    },
    '& .MuiDataGrid-columnHeaderTitle': {
      width: '100%',
      textOverflow: 'initial',
    },
  },
}));

interface UserConfiguredDataGridProProps {
  tableName: string;
  readOnly?: boolean;
  onNewLegAddClick?: () => void;
  onExportLoadingList?: () => void;
  showRowCountOnHeader?: number;
  summaryData?: {
    totalOrders: number;
    totalSum: {
      pallets: number;
      weight: number;
      packages: number;
      palletSpace: number;
    };
  };
  handleLocationMapping?: () => void;
  handleDeleteOrders?: () => void;
  disableLocationButton?: boolean;
  shipmentOrderType?: ShipmentOrderView;
  handleHideOrders?: () => void;
  handleBulkLinkUnlink?: () => void;
  disableBulkLinkButton?: boolean;
  toggleRowHeight?: {
    isChecked: boolean;
    onToggleRowHeight: () => void;
  };
  toggleColumnWidth?: {
    isAdjusted: boolean;
    onToggleColumnWidth: () => void;
  };
}

interface ComponentsToRenderType {
  component: FC;
  props?: any;
}
// Custom operator: does not contain

export const doesNotContainOperator = {
  label: 'does not contain',
  value: 'doesNotContain',
  getApplyFilterFn: (filterItem: GridFilterItem) => {
    if (
      filterItem.value === '' ||
      filterItem.value == null ||
      !filterItem.columnField
    ) {
      return null;
    }
    return (params: { value?: any }) => {
      const { value } = params;
      const filterValue = filterItem.value;
      // If both are numbers, compare as numbers
      if (
        typeof value === 'number' &&
        !isNaN(value) &&
        typeof filterValue === 'number' &&
        !isNaN(filterValue)
      ) {
        return !String(value).includes(String(filterValue));
      }
      // Otherwise, compare as strings (case-insensitive)
      return !String(value ?? '')
        .toLowerCase()
        .includes(String(filterValue ?? '').toLowerCase());
    };
  },
  InputComponent: GridFilterInputValue,
};
// Custom operator: does not equal

export const doesNotEqualOperator = {
  label: 'does not equal',
  value: 'doesNotEqual',
  getApplyFilterFn: (filterItem: GridFilterItem) => {
    if (filterItem.value === undefined || filterItem.value === null) {
      return null;
    }
    return (params: { value?: any }) => {
      const { value } = params;
      const filterValue = filterItem.value;
      // If both are numbers, compare as numbers
      if (
        typeof value === 'number' &&
        !isNaN(value) &&
        typeof filterValue === 'number' &&
        !isNaN(filterValue)
      ) {
        return value !== filterValue;
      }
      return (
        String(value ?? '').toLowerCase() !==
        String(filterItem.value).toLowerCase()
      );
    };
  },
  InputComponent: GridFilterInputValue,
};

const LOCATION_AUXILIARY_FIELDS = new Set(['delete']);

function isColumnVisible(
  model: GridColumnVisibilityModel,
  field: string,
): boolean {
  return model[field] !== false;
}

function getProtectedColumnFields(
  tableName: string,
  columns: readonly GridColDef[],
): string[] {
  if (tableName !== TABLE_NAMES.Locations) {
    return [];
  }
  return columns
    .map((column) => column.field)
    .filter(
      (field) =>
        !field.startsWith('__') && !LOCATION_AUXILIARY_FIELDS.has(field),
    );
}

export function UserConfiguredDataGridPro(
  props: UserConfiguredDataGridProProps & ComponentProps<typeof DataGridPro>,
) {
  const apiRef = props.apiRef ?? useGridApiRef();
  const {
    getFilterModel,
    getSortModel,
    getGridDensity,
    getColumnsConfiguration,
    attachConfiguration,
    updateColumnConfiguration,
    updateColumnVisibilityModelConfiguration,
    updateFilterConfiguration,
    updateGridDensity,
    updateSortConfiguration,
  } = useUserConfiguration();
  const [densitySet, setDensitySet] = useState(false);
  const classes = useStyles();
  const {
    showRowCountOnHeader,
    summaryData,
    tableName,
    columns,
    components,
    readOnly = false,
    onNewLegAddClick = null,
    onExportLoadingList = null,
    handleLocationMapping,
    handleDeleteOrders,
    disableLocationButton = true,
    shipmentOrderType,
    handleHideOrders,
    handleBulkLinkUnlink,
    disableBulkLinkButton = true,
    toggleRowHeight,
    toggleColumnWidth,
  } = props;
  const columnsConfiguration = getColumnsConfiguration(tableName);
  const userConfiguredColumns = useMemo(() => {
    return attachConfiguration(columns, columnsConfiguration);
  }, [columnsConfiguration, tableName, columns]);
  const setVisibilityModelDefault = (): GridColumnVisibilityModel => {
    //set default column visibility based on table/grid
    if (tableName) {
      switch (tableName) {
        case TABLE_NAMES.TruckFillAndTime:
          return {
            id: false,
            newArrivalTime: false,
            arrivalCountry: false,
            departureCountry: false,
            carCarrierId: false,
            carCarrierIdQR: false,
            trailerCarrierId: false,
            trailerCarrierIdQR: false,
          };
        case TABLE_NAMES.FixedTrafficOverview:
          return {
            id: false,
          };
        case TABLE_NAMES.ShipmentList:
          return {
            id: false,
          };
        case TABLE_NAMES.TFTReadOnly:
          return {
            id: false,
          };
        case TABLE_NAMES.TourRouteTemplates:
          return {
            id: false,
            endExceptionDate: false,
            startExceptionDate: false,
            startDate: false,
            endDate: false,
            dispatcher: false,
            fuel: false,
            lastUpdated: false,
            lastUpdatedBy: false,
            createdBy: false,
            days: false,
            note: false,
            externalNote: false,
          };
        case TABLE_NAMES.TourTemplates:
          return {
            createdBy: false,
            lastUpdatedBy: false,
            lastUpdated: false,
            fuel: false,
            dispatcher: false,
            endExceptionDate: false,
            startExceptionDate: false,
          };
        case TABLE_NAMES.OtherTours:
          return {
            createdBy: false,
            lastUpdatedBy: false,
            lastUpdated: false,
          };
        case TABLE_NAMES.ViewSporadicRouteTemplates:
          return {
            id: false,
          };
        case TABLE_NAMES.Checkpoints:
        case TABLE_NAMES.Customers:
        case TABLE_NAMES.Terminals:
        case TABLE_NAMES.Subcontractors:
          return {
            id: false,
          };
        case TABLE_NAMES.RemainingGoodsReport:
          return {
            id: false,
            createdBy: false,
            lastUpdatedBy: false,
          };
        case TABLE_NAMES.RouteDeviations:
          return {
            arrivalCity: false,
            departureCity: false,
            routeLicensePlate: false,
            routeCapacity: false,
          };
        case TABLE_NAMES.FixedRoutes:
        case TABLE_NAMES.SporadicRoutes:
          return {
            id: false,
            routeId: false,
            tour: false,
            fuel: false,
            driverName: false,
            driverPhoneNumber: false,
            licensePlate: false,
          };
        case TABLE_NAMES.RouteLegInReport:
          return {
            id: false,
          };
        case TABLE_NAMES.PeakPlanning:
        case TABLE_NAMES.PeakPlanningDebit:
          return {
            id: !configuration.production,
          };
        case TABLE_NAMES.ShipmentGrid:
          return {
            id: false,
          };
        case TABLE_NAMES.EditableLegLevelGrid:
          return {
            legsLength: false,
          };
        case TABLE_NAMES.MatchingShipment:
          return {
            id: false,
            routeLockedBy: false,
            routeLockedAt: false,
            isChecklistLocked: false,
            ping: false,
            unlock: false,
          };
        default:
          return {};
      }
    } else return {};
  };
  const defaultVisibilityModel = useMemo(
    () => setVisibilityModelDefault(),
    [tableName],
  );
  const protectedColumnFields = useMemo(
    () => getProtectedColumnFields(tableName, columns ?? []),
    [columns, tableName],
  );
  const [gridColumnVisibilityModel, setGridColumnVisibilityModel] =
    useState<GridColumnVisibilityModel>(defaultVisibilityModel);
  const [currentGridDensity, setCurrentGridDensity] = useState<
    GridDensity | undefined
  >(getGridDensity(tableName));
  const hasVisibleProtectedColumn = useCallback(
    (model: GridColumnVisibilityModel) => {
      if (protectedColumnFields.length === 0) {
        return true;
      }
      return protectedColumnFields.some((field) =>
        isColumnVisible(model, field),
      );
    },
    [protectedColumnFields],
  );
  const recoverVisibilityModel = useCallback(
    (model: GridColumnVisibilityModel) => {
      if (hasVisibleProtectedColumn(model)) {
        return model;
      }
      return defaultVisibilityModel;
    },
    [defaultVisibilityModel, hasVisibleProtectedColumn],
  );
  const guardVisibilityModel = useCallback(
    (model: GridColumnVisibilityModel) => {
      if (hasVisibleProtectedColumn(model)) {
        return model;
      }
      return props.columnVisibilityModel ?? gridColumnVisibilityModel;
    },
    [
      gridColumnVisibilityModel,
      hasVisibleProtectedColumn,
      props.columnVisibilityModel,
    ],
  );
  useEffect(() => {
    if (columnsConfiguration == null) {
      setGridColumnVisibilityModel(defaultVisibilityModel);
      return;
    }
    const configuredVisibilityModel: GridColumnVisibilityModel = {
      ...defaultVisibilityModel,
    };
    Object.entries(columnsConfiguration).forEach(
      ([field, columnConfiguration]) => {
        if (
          columnConfiguration != null &&
          Object.prototype.hasOwnProperty.call(columnConfiguration, 'hide')
        ) {
          configuredVisibilityModel[field] = !columnConfiguration.hide;
        }
      },
    );
    const nextVisibilityModel = recoverVisibilityModel(
      configuredVisibilityModel,
    );
    setGridColumnVisibilityModel(nextVisibilityModel);
    if (!_.isEqual(nextVisibilityModel, configuredVisibilityModel)) {
      updateColumnVisibilityModelConfiguration(tableName, nextVisibilityModel);
    }
  }, [
    columnsConfiguration,
    defaultVisibilityModel,
    recoverVisibilityModel,
    tableName,
    updateColumnVisibilityModelConfiguration,
  ]);
  // Sync internal visibility state when controlled prop changes externally
  useEffect(() => {
    if (
      props.columnVisibilityModel != null &&
      !_.isEqual(props.columnVisibilityModel, gridColumnVisibilityModel)
    ) {
      setGridColumnVisibilityModel(props.columnVisibilityModel);
    }
  }, [props.columnVisibilityModel]);
  useEffect(() => {
    if (apiRef?.current?.setDensity != null) {
      apiRef.current.setDensity(currentGridDensity ?? GridDensityTypes.Compact);
      setDensitySet(true);
    }
  }, [apiRef]);
  const filterModel = getFilterModel(tableName);
  const sortModel = getSortModel(tableName);
  const customComponents = {
    ...components,
    Toolbar: components?.Toolbar ?? CustomGridToolbar,
    ColumnsPanel: components?.ColumnsPanel ?? CustomColumnsPanel,
  };
  const onChangeColumnVisibilityModel = useCallback(
    (newModel: GridColumnVisibilityModel) => {
      const nextModel = guardVisibilityModel(newModel);
      // Compare against the controlled prop (what the grid actually renders)
      // falling back to internal state for uncontrolled mode
      const currentModel =
        props.columnVisibilityModel ?? gridColumnVisibilityModel;
      const changedField = Array.from(
        new Set([...Object.keys(currentModel), ...Object.keys(nextModel)]),
      ).filter(
        (field) =>
          isColumnVisible(currentModel, field) !==
          isColumnVisible(nextModel, field),
      );
      if (changedField.length === 0) {
        return nextModel;
      }
      if (
        tableName !== TABLE_NAMES.TruckFillAndTime &&
        tableName !== TABLE_NAMES.NewShipmentLoadingList &&
        (tableName !== TABLE_NAMES.TFTReadOnly || changedField.length > 1)
      ) {
        updateColumnVisibilityModelConfiguration(tableName, nextModel);
      }
      setGridColumnVisibilityModel(nextModel);
      return nextModel;
    },
    [
      gridColumnVisibilityModel,
      guardVisibilityModel,
      props.columnVisibilityModel,
      tableName,
      updateColumnVisibilityModelConfiguration,
    ],
  );
  const onExportClicked = useCallback(() => {
    const delimiter = ';#';
    const csvString = apiRef.current.getDataAsCsv({ delimiter });
    // Collapse newlines inside quoted CSV fields so they stay in one cell
    const sanitizedCsv = csvString.replace(/"[^"]*"/g, (match) =>
      match.replace(/\r?\n/g, ' '),
    );
    const arrayOfArrayCsv = sanitizedCsv.split('\n').map((row: string) => {
      return row.split(delimiter);
    });
    if (
      tableName === TABLE_NAMES.SporadicRoutes &&
      arrayOfArrayCsv.length > 1
    ) {
      const allHeaders = arrayOfArrayCsv[0].map((str) => {
        return str.toLowerCase();
      });
      let priceColumnIndex = allHeaders.indexOf('agreed price');
      if (priceColumnIndex == -1)
        priceColumnIndex = allHeaders.indexOf('agreed price\r');
      if (priceColumnIndex >= 0) {
        for (let i = 1; i < arrayOfArrayCsv.length; i++) {
          if (
            arrayOfArrayCsv[i][priceColumnIndex] &&
            typeof arrayOfArrayCsv[i][priceColumnIndex] !== 'undefined' &&
            arrayOfArrayCsv[i][priceColumnIndex].trim().length > 3 &&
            arrayOfArrayCsv[i][priceColumnIndex].trim().includes(' ')
          )
            arrayOfArrayCsv[i][priceColumnIndex] = arrayOfArrayCsv[i][
              priceColumnIndex
            ].replace(/ /g, '');
        }
      }
    }
    const wb = XLSX.utils.book_new();
    const newWs = XLSX.utils.aoa_to_sheet(arrayOfArrayCsv);
    XLSX.utils.book_append_sheet(wb, newWs);
    XLSX.writeFile(wb, `${tableName}.xlsx`);
  }, [apiRef]);
  const onAddNewLeg = () => {
    if (onNewLegAddClick) onNewLegAddClick();
  };
  const [pageSize, setPageSize] = useState<number>(200);
  return (
    <DataGridPro
      className={classes.root}
      disableColumnReorder
      {...props}
      components={customComponents}
      componentsProps={{
        toolbar: {
          onExportClicked,
          onExportLoadingList,
          tableName,
          onAddNewLeg,
          readOnly,
          showRowCountOnHeader,
          showQuickFilter: true,
          quickFilterProps: { debounceMs: 500 },
          summaryData,
          handleLocationMapping,
          handleDeleteOrders,
          disableLocationButton,
          shipmentOrderType,
          handleHideOrders,
          handleBulkLinkUnlink,
          disableBulkLinkButton,
          toggleRowHeight,
          toggleColumnWidth,
        },
      }}
      columnTypes={{
        string: {
          filterOperators: [
            ...getGridStringOperators(),
            doesNotContainOperator,
            doesNotEqualOperator,
          ],
        },
        number: {
          filterOperators: [
            ...getGridNumericOperators(),
            doesNotContainOperator,
            doesNotEqualOperator,
          ],
        },
      }}
      hideFooter={true}
      headerHeight={props.headerHeight || 100}
      apiRef={apiRef}
      disableColumnMenu={false}
      columns={userConfiguredColumns}
      filterModel={props.filterModel ? props.filterModel : filterModel}
      sortModel={props.sortModel ?? sortModel}
      pinnedColumns={props?.pinnedColumns}
      pageSize={pageSize}
      onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
      rowsPerPageOptions={[50, 100, 200]}
      pagination={props.pagination ?? false}
      onStateChange={(state) => {
        if (densitySet && state.density.value !== currentGridDensity) {
          setCurrentGridDensity(state.density.value);
          if (
            tableName !== TABLE_NAMES.TruckFillAndTime &&
            tableName !== TABLE_NAMES.EditableLegLevelGrid &&
            tableName !== TABLE_NAMES.CustomsTFTLegGrid
          )
            updateGridDensity(tableName, state.density.value);
        }
        if (
          (tableName === TABLE_NAMES.RemainingGoods && props?.onStateChange) ||
          (tableName === TABLE_NAMES.TruckFillAndTime &&
            props?.onStateChange) ||
          (tableName === TABLE_NAMES.TFTReadOnly && props?.onStateChange) ||
          (tableName === TABLE_NAMES.FixedRoutes && props?.onStateChange) ||
          (tableName === TABLE_NAMES.RemainingGoodsReport &&
            props?.onStateChange) ||
          (tableName === TABLE_NAMES.ShipmentGrid && props?.onStateChange) ||
          (tableName === TABLE_NAMES.NewShipmentLoadingList &&
            props?.onStateChange) ||
          (tableName === TABLE_NAMES.TourRouteTemplates && props?.onStateChange)
        ) {
          return props.onStateChange(state, {}, {});
        }
      }}
      onFilterModelChange={(...args) => {
        const [model] = args;
        if (
          tableName !== TABLE_NAMES.TFTReadOnly &&
          tableName !== TABLE_NAMES.TourRouteTemplates &&
          tableName !== TABLE_NAMES.CustomsTFTRouteGrid &&
          tableName !== TABLE_NAMES.CustomsTFTLegGrid &&
          tableName !== TABLE_NAMES.Locations &&
          tableName !== TABLE_NAMES.Assets &&
          !model.quickFilterValues
        ) {
          updateFilterConfiguration(tableName, model);
        }
        if (props.onFilterModelChange != null) {
          return props.onFilterModelChange(...args);
        }
        model.quickFilterLogicOperator = GridLinkOperator.And;
      }}
      onSortModelChange={(...args) => {
        const [model] = args;
        const interestingProperties = ['columnField', 'operatorValue', 'value'];
        if (
          tableName !== TABLE_NAMES.TFTReadOnly &&
          tableName !== TABLE_NAMES.TourRouteTemplates &&
          tableName !== TABLE_NAMES.CustomsTFTLegGrid &&
          tableName !== TABLE_NAMES.Locations
        ) {
          if (
            !isEqual(
              pick(filterModel, interestingProperties),
              pick(model, interestingProperties),
            )
          ) {
            updateSortConfiguration(tableName, model);
          }
        }
        if (props.onSortModelChange != null) {
          return props.onSortModelChange(...args);
        }
      }}
      onColumnWidthChange={(...args) => {
        // --- NEW: Prevent saving configuration when the toggle is active ---
        if (props.toggleColumnWidth?.isAdjusted) {
          return;
        }
        // -------------------------------------------------------------------
        const [params] = args;
        const colDef = params.colDef;
        const field = colDef.field;
        if (
          tableName !== TABLE_NAMES.TruckFillAndTime &&
          tableName !== TABLE_NAMES.TFTReadOnly &&
          tableName !== TABLE_NAMES.TourRouteTemplates &&
          tableName !== TABLE_NAMES.EditableLegLevelGrid &&
          tableName !== TABLE_NAMES.CustomsTFTLegGrid
        ) {
          updateColumnConfiguration(tableName, field, {
            width: params.width,
          });
        }
        if (props.onColumnWidthChange != null) {
          return props.onColumnWidthChange(...args);
        }
      }}
      columnVisibilityModel={
        props.columnVisibilityModel ?? gridColumnVisibilityModel
      }
      onColumnVisibilityModelChange={(model, ...args) => {
        const nextModel = onChangeColumnVisibilityModel(model);
        if (props.onColumnVisibilityModelChange != null) {
          props.onColumnVisibilityModelChange(nextModel, ...args);
        }
      }}
      checkboxSelection={props.checkboxSelection}
      selectionModel={props.selectionModel}
      onSelectionModelChange={props.onSelectionModelChange}
      isRowSelectable={props.isRowSelectable}
      disableSelectionOnClick={props.disableSelectionOnClick ?? true}
    />
  );
}

const columnsPanelUseStyles = makeStyles(() => ({
  root: {
    display: 'flex',
    minWidth: '300px',
    maxHeight: '450px',
    flexDirection: 'row',
    width: '100%',
    '& .MuiGridPanelFooter-root': {
      display: 'none',
    },
  },
}));

export function CustomColumnsPanel() {
  const classes = columnsPanelUseStyles();
  return (
    <Box className={classes.root}>
      <GridColumnsPanel />
    </Box>
  );
}

export interface CustomGridToolbarProps {
  onExportClicked: () => void;
  onExportLoadingList: () => void;
  tableName: string;
  onAddNewLeg: () => void;
  readOnly?: boolean;
  showRowCountOnHeader?: number;
  toggleRowHeight?: {
    isChecked: boolean;
    onToggleRowHeight: () => void;
  };
  toggleColumnWidth?: {
    isAdjusted: boolean;
    onToggleColumnWidth: () => void;
  };
  handlePrint?: () => void;
  componentsToRender?: ComponentsToRenderType[];
  summaryData?: {
    totalOrders: number;
    totalSum: {
      pallets: number;
      weight: number;
      packages: number;
      palletSpace: number;
    };
  };
  handleLocationMapping?: () => void;
  handleDeleteOrders?: () => void;
  disableLocationButton?: boolean;
  shipmentOrderType?: ShipmentOrderView;
  handleHideOrders?: () => void;
  handleBulkLinkUnlink?: () => void;
  disableBulkLinkButton?: boolean;
}

export function CustomGridToolbar(props: CustomGridToolbarProps) {
  const { t } = useTranslation();
  const {
    onExportClicked,
    onExportLoadingList,
    tableName,
    onAddNewLeg,
    readOnly = false,
    showRowCountOnHeader,
    toggleRowHeight,
    toggleColumnWidth,
    handlePrint,
    componentsToRender,
    summaryData,
    handleLocationMapping,
    handleDeleteOrders,
    disableLocationButton,
    shipmentOrderType,
    handleHideOrders,
    handleBulkLinkUnlink,
    disableBulkLinkButton,
  } = props;
  return (
    <GridToolbarContainer>
      {tableName === TABLE_NAMES.EditableLegLevelGrid ? (
        <Button
          size="small"
          variant="contained"
          disabled={readOnly}
          onClick={onAddNewLeg}
          sx={{
            marginRight: '10px',
            backgroundColor: '#68bc46',
            color: '#000',
            '&.Mui-disabled': {
              backgroundColor: 'action.disabledBackground',
              color: 'action.disabled',
            },
          }}
        >
          {'Add Leg'}
        </Button>
      ) : null}
      <GridToolbarColumnsButton
        style={{ color: '#69bc46' }}
        onResize={undefined}
        onResizeCapture={undefined}
      />
      <GridToolbarFilterButton
        style={{ color: '#69bc46' }}
        onResize={undefined}
        onResizeCapture={undefined}
      />
      <GridToolbarDensitySelector
        style={{ color: '#69bc46' }}
        onResize={undefined}
        onResizeCapture={undefined}
      />
      <Button
        style={{ color: '#69bc46' }}
        startIcon={<SaveAltIcon />}
        size="small"
        onClick={onExportClicked}
      >
        {`${t('attributes.getFilteredExcel')}`}
      </Button>
      {tableName === TABLE_NAMES.TruckFillAndTime ? (
        <Tooltip title={`${t('attributes.sortingInfo')}`}>
          <Typography style={{ marginLeft: 7, fontSize: 13 }}>
            {`(ℹ️)${t('attributes.sortingIconText')}`}
          </Typography>
        </Tooltip>
      ) : null}
      {tableName === TABLE_NAMES.EditableLegLevelGrid ? (
        <Button
          style={{ color: '#69bc46' }}
          startIcon={<SaveAltIcon />}
          size="small"
          onClick={onExportLoadingList}
        >
          {t('actions.export', { item: 'Loading list' })}
        </Button>
      ) : null}
      {tableName === TABLE_NAMES.ShipmentGrid ||
      tableName === TABLE_NAMES.ShipmentList ||
      tableName === TABLE_NAMES.NewShipmentLoadingList ||
      tableName === TABLE_NAMES.TourRouteTemplates ||
      tableName === TABLE_NAMES.CustomsTFTRouteGrid ||
      tableName === TABLE_NAMES.Locations ||
      tableName === TABLE_NAMES.Assets ? (
        <div>
          {toggleRowHeight ? (
            <Checkbox
              onChange={toggleRowHeight.onToggleRowHeight}
              checked={toggleRowHeight.isChecked}
              icon={<Expand style={{ color: '#69bc46' }} />}
              checkedIcon={<Compress color="action" />}
              title={
                toggleRowHeight.isChecked
                  ? t('attributes.compactRowHeight')
                  : t('attributes.dynamicRowHeight')
              }
              inputProps={{
                'aria-label': toggleRowHeight.isChecked
                  ? t('attributes.compactRowHeight')
                  : t('attributes.dynamicRowHeight'),
              }}
            />
          ) : null}
          {toggleColumnWidth ? (
            <Checkbox
              onChange={toggleColumnWidth.onToggleColumnWidth}
              checked={toggleColumnWidth.isAdjusted}
              icon={<ViewColumnIcon style={{ color: '#69bc46' }} />}
              checkedIcon={<ViewColumnIcon color="action" />}
              title={
                toggleColumnWidth.isAdjusted
                  ? 'Revert Column Width'
                  : 'Adjust Column Width'
              }
              inputProps={{
                'aria-label': toggleColumnWidth.isAdjusted
                  ? 'Revert Column Width'
                  : 'Adjust Column Width',
              }}
            />
          ) : null}
          <GridToolbarQuickFilter
            style={{ marginLeft: '2px' }}
            sx={{
              '& .MuiInputBase-root': {
                height: { xs: '28px', sm: '32px' },
                fontSize: { xs: '0.75rem', sm: '0.875rem' },
                minWidth: { xs: '120px', sm: '160px', md: '180px' },
                borderRadius: '4px',
                '&:before': {
                  display: 'none !important',
                },
                '&:after': {
                  display: 'none !important',
                },
                '&:hover:not(.Mui-disabled):before': {
                  display: 'none !important',
                },
              },
              '& .MuiFormControl-root': {
                minWidth: { xs: '120px', sm: '160px', md: '180px' },
                maxWidth: { xs: '150px', sm: '200px', md: '220px' },
              },
              '& .MuiInputBase-input': {
                padding: { xs: '2px 4px', sm: '2px 4px' },
                fontSize: { xs: '0.75rem', sm: '0.875rem' },
                '&::placeholder': {
                  color: '#999',
                  opacity: 1,
                },
              },
              '& .MuiInput-underline:before': {
                display: 'none !important',
              },
              '& .MuiInput-underline:after': {
                display: 'none !important',
              },
              '& .MuiInput-underline:hover:not(.Mui-disabled):before': {
                display: 'none !important',
              },
              '& .MuiOutlinedInput-root': {
                height: { xs: '28px', sm: '32px' },
                backgroundColor: '#f5f5f5',
                '& fieldset': {
                  border: 'none',
                },
                '&:hover fieldset': {
                  border: 'none',
                },
                '&.Mui-focused fieldset': {
                  border: 'none',
                },
              },
              '& .MuiOutlinedInput-notchedOutline': {
                border: 'none !important',
              },
              // keep search icon visible and avoid hiding svg/adornment
              // Responsive margin and positioning
              marginLeft: { xs: '8px', sm: '12px' },
              width: { xs: 'auto', sm: 'auto', md: 'auto' },
              maxWidth: { xs: '150px', sm: '200px', md: '220px' },
            }}
            placeholder="Search..."
          />
        </div>
      ) : null}
      {tableName === TABLE_NAMES.NewShipmentLoadingList && summaryData ? (
        <Box
          display="flex"
          gap={1}
          alignItems="center"
          style={{ marginLeft: 'auto', marginRight: 10 }}
        >
          <Tooltip title={`${t('attributes.tooltipLinkingInfo')}`}>
            <span>
              <Button
                disabled={disableBulkLinkButton}
                onClick={handleBulkLinkUnlink}
                size="small"
                startIcon={<LinkIcon />}
                variant="contained"
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                }}
              >
                {t('attributes.link', { defaultValue: 'Link' })}
              </Button>
            </span>
          </Tooltip>
          <Button
            disabled={disableLocationButton || shipmentOrderType == 'hidden'}
            onClick={handleHideOrders}
            size="small"
            startIcon={<HideIcon />}
            variant="contained"
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
            }}
          >
            {t('attributes.hideAction')}
          </Button>
          {shipmentOrderType == 'nexus' ? (
            <Button
              disabled={disableLocationButton}
              onClick={handleDeleteOrders}
              size="small"
              startIcon={<DeleteIcon />}
              variant="contained"
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
              }}
            >
              {t('attributes.deleteAction')}
            </Button>
          ) : null}
          {shipmentOrderType !== 'hidden' ? (
            <Button
              disabled={disableLocationButton}
              onClick={handleLocationMapping}
              size="small"
              startIcon={<AddIcon />}
              variant="contained"
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
              }}
            >
              {t('attributes.performAction')}
            </Button>
          ) : null}
          <Typography
            variant="body2"
            fontWeight="bold"
            style={{ color: '#69bc46' }}
          >
            {`ROWS : ${Math.round(summaryData.totalOrders || 0)} `}
          </Typography>
          <Typography
            variant="body2"
            fontWeight="bold"
            style={{ color: '#69bc46' }}
          >
            {`PCS : ${Math.round(summaryData.totalSum?.packages || 0)} `}
          </Typography>
          <Typography
            variant="body2"
            fontWeight="bold"
            style={{ color: '#69bc46' }}
          >
            {`PAL : ${Math.round(summaryData.totalSum?.pallets || 0)} `}
          </Typography>
          <Typography
            variant="body2"
            fontWeight="bold"
            style={{ color: '#69bc46' }}
          >
            {`PPL : ${Math.round(summaryData.totalSum?.palletSpace || 0)} `}
          </Typography>
          <Typography
            variant="body2"
            fontWeight="bold"
            style={{ color: '#69bc46' }}
          >
            {`WT : ${Math.round(summaryData.totalSum?.weight || 0)}`}
          </Typography>
        </Box>
      ) : null}
      {typeof showRowCountOnHeader !== 'undefined' &&
      tableName !== TABLE_NAMES.NewShipmentLoadingList ? (
        <Typography
          style={{
            position: 'absolute',
            right: '5px',
            fontSize: '0.875rem',
          }}
        >
          {t('attributes.totalRows', {
            rowCount: showRowCountOnHeader,
          })}
        </Typography>
      ) : null}
      {toggleRowHeight &&
      tableName !== TABLE_NAMES.NewShipmentLoadingList &&
      tableName !== TABLE_NAMES.CustomsTFTRouteGrid &&
      tableName !== TABLE_NAMES.TourRouteTemplates &&
      tableName !== TABLE_NAMES.ShipmentGrid ? (
        <>
          <Checkbox
            onChange={toggleRowHeight.onToggleRowHeight}
            checked={toggleRowHeight.isChecked}
            icon={<Expand style={{ color: '#69bc46' }} />}
            checkedIcon={<Compress color="action" />}
            title={
              toggleRowHeight.isChecked
                ? t('attributes.compactRowHeight')
                : t('attributes.dynamicRowHeight')
            }
            inputProps={{
              'aria-label': toggleRowHeight.isChecked
                ? t('attributes.compactRowHeight')
                : t('attributes.dynamicRowHeight'),
            }}
          />
          {toggleColumnWidth ? (
            <Checkbox
              onChange={toggleColumnWidth.onToggleColumnWidth}
              checked={toggleColumnWidth.isAdjusted}
              icon={<ViewColumnIcon style={{ color: '#69bc46' }} />}
              checkedIcon={<ViewColumnIcon color="action" />}
              title={
                toggleColumnWidth.isAdjusted
                  ? 'Revert Column Width'
                  : 'Adjust Column Width'
              }
              inputProps={{
                'aria-label': toggleColumnWidth.isAdjusted
                  ? 'Revert Column Width'
                  : 'Adjust Column Width',
              }}
            />
          ) : null}
        </>
      ) : null}
      {handlePrint ? (
        <IconButton
          size="small"
          onClick={handlePrint}
          style={{ color: '#69bc46' }}
          title={t('attributes.print')}
        >
          <Print />
        </IconButton>
      ) : null}
      {componentsToRender
        ? componentsToRender.map(({ component: Comp, props = {} }, index) => (
            <Comp key={index} {...props} />
          ))
        : null}
    </GridToolbarContainer>
  );
}
